import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { App, Button, Form, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import Truncate from 'v2/components/Shared/TruncateText';
import { useListConnectActions } from 'v2/hooks/useListConnectActions';
import { useListConnectOptions } from 'v2/hooks/useListConnectOptions';
import { useListTriggers } from 'v2/hooks/useListTriggers';
import { useLoadConnect } from 'v2/hooks/useLoadConnect';
import { connectService } from 'v2/services/connect';

import { Icon } from '@eduzz/ui-antd-theme';

import EditAction from '../components/EditAction';
import NewAction from '../components/NewAction';
import { useActionState } from '../contexts/ActionStateContext';
import { useConnectState } from '../contexts/ConnectStateContext';

const Actions: React.FC = () => {
  const { message } = App.useApp();
  const { currentConnect, setCurrentConnect } = useConnectState();
  const { openNewActionModal, setCurrentAction, openEditActionModal } = useActionState();
  const [filterAction, setFilterAction] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');

  const history = useHistory();
  const { connectId } = useParams<{ connectId: string }>();

  const { actions, isLoading: isFetching } = useListConnectActions(Number(connectId));
  const { connectData, error: loadConnectError } = useLoadConnect(connectId ? Number(connectId) : undefined);
  const { triggers } = useListTriggers();
  const { options } = useListConnectOptions(currentConnect?.autoresponder_id);

  useEffect(() => {
    if (!connectId) {
      history.push('/producer/connect/myconnects/');
    }
  }, [connectId, history]);

  useEffect(() => {
    if (connectData) {
      setCurrentConnect(connectData);
    }
  }, [connectData, setCurrentConnect]);

  useEffect(() => {
    if (loadConnectError) {
      message.error('Erro ao carregar connect');
      history.push('/producer/connect/myconnects/');
    }
  }, [loadConnectError, history, message]);

  const openModal = useCallback(() => {
    openNewActionModal();
  }, [openNewActionModal]);

  const openModalEdit = useCallback(
    async (action: any) => {
      try {
        const result = await connectService.loadAction(currentConnect.id, action.id);
        const loadedAction = result?.data ?? result;
        setCurrentAction(loadedAction);
        openEditActionModal();
      } catch (error: any) {
        message.error(error?.message ?? 'Erro ao carregar ação');
      }
    },
    [currentConnect, setCurrentAction, openEditActionModal, message]
  );

  const listActions = Array.isArray(actions) ? actions : [];

  const filteredOptions = listActions.filter((la: any) => {
    if (filterAction && filterAction !== '' && la.option_id != filterAction) return false;
    if (filterTrigger && filterTrigger !== '' && la.trigger_id != filterTrigger) return false;
    return true;
  });

  const triggerSelectOptions = [
    { value: '', label: 'Todos' },
    ...(triggers && Array.isArray(triggers)
      ? triggers.map((option: any) => ({ value: option.id, label: option.name }))
      : [])
  ];

  const actionSelectOptions = [
    { value: '', label: 'Todas' },
    ...(options && Array.isArray(options)
      ? options.map((option: any) => ({ value: option.id, label: option.title }))
      : [])
  ];

  const columns: ColumnsType<any> = [
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'status',
      render: (active: boolean) => <Tag color={active ? 'success' : 'error'}>{active ? 'Ativo' : 'Inativo'}</Tag>
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Produto',
      key: 'product',
      render: (_: unknown, record: any) => (
        <div className='flex flex-col'>
          <Typography.Text strong>{record.product_id}</Typography.Text>
          <Truncate final='...' count={50}>
            {record.product_title}
          </Truncate>
        </div>
      )
    },
    {
      title: 'Gatilho',
      dataIndex: 'trigger',
      key: 'trigger'
    },
    {
      title: 'Ação',
      dataIndex: 'action',
      key: 'action'
    },
    {
      title: '',
      key: 'edit',
      width: 120,
      align: 'right',
      render: (_: unknown, record: any) => (
        <PermissionHide action='myeduzz:connect:editar'>
          <Button type='text' icon={<Icon.Pencil size={16} />} onClick={() => openModalEdit(record)}>
            Editar
          </Button>
        </PermissionHide>
      )
    }
  ];

  return (
    <>
      <TopPage
        title='Lista de ações'
        action={
          <PermissionHide action='myeduzz:connect:criar'>
            <Button icon={<Icon.Plus size={16} />} type='primary' onClick={openModal}>
              Nova ação
            </Button>
          </PermissionHide>
        }
      />

      <div className='flex flex-col gap-4'>
        <div className='grid grid-cols-1 items-end gap-4 md:grid-cols-12'>
          <div className='flex items-center gap-3 md:col-span-6'>
            {currentConnect?.image && (
              <img
                src={currentConnect.image}
                alt={currentConnect?.name || 'Connect'}
                className='h-9 max-w-20 object-contain'
              />
            )}
            <div className='min-w-0'>
              <Typography.Text strong className='block'>
                {currentConnect?.name}
              </Typography.Text>
              <Typography.Text type='secondary' className='block text-sm'>
                {currentConnect?.description}
              </Typography.Text>
              <Typography.Text strong className='mt-1 block text-sm'>
                {currentConnect?.parameters?.[0]?.label}
              </Typography.Text>
              <Truncate count={50} final='...'>
                {currentConnect?.parameters?.[0]?.value}
              </Truncate>
            </div>
          </div>

          <Form layout='vertical' className='md:col-span-3'>
            <Form.Item label='Gatilho' className='mb-0'>
              <Select
                value={filterTrigger}
                onChange={value => setFilterTrigger(value as string)}
                className='w-full'
                options={triggerSelectOptions}
              />
            </Form.Item>
          </Form>

          <Form layout='vertical' className='md:col-span-3'>
            <Form.Item label='Ação' className='mb-0'>
              <Select
                value={filterAction}
                onChange={value => setFilterAction(value as string)}
                className='w-full'
                options={actionSelectOptions}
              />
            </Form.Item>
          </Form>
        </div>

        <Table
          columns={columns}
          dataSource={filteredOptions}
          rowKey={(record: any) => `connect-${record.id}`}
          loading={isFetching}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nenhuma ação encontrada' }}
        />
      </div>

      <NewAction />
      <EditAction />
    </>
  );
};

export default Actions;
